import { useState, useEffect } from 'react';
import { crmService } from '../../services/crmService';

const FIELDS = [
  { key: 'name',           label: 'Full Name',  aliases: ['name', 'full name', 'member', 'employee'] },
  { key: 'first_name',     label: 'First Name', aliases: ['first name', 'firstname', 'first', 'given name'] },
  { key: 'last_name',      label: 'Last Name',  aliases: ['last name', 'lastname', 'last', 'surname'] },
  { key: 'email',          label: 'Email',      aliases: ['email', 'e-mail', 'email address', 'username', 'user'] },
  { key: 'position_title', label: 'Position',   aliases: ['position', 'title', 'job title', 'role', 'duty title'] },
  { key: 'team_group',     label: 'Team',       aliases: ['team', 'team group', 'group', 'department', 'element'] },
  { key: 'phone',          label: 'Phone',      aliases: ['phone', 'phone number', 'mobile', 'cell'] },
];

const autoMap = (header) => {
  const h = String(header || '').trim().toLowerCase();
  const f = FIELDS.find(f => f.key === h || f.aliases.includes(h));
  return f ? f.key : '';
};

const TEMPLATE_CSV = 'name,email,position,team,phone\n'
  + 'Ada Lovelace,ada,Lead Autonomy Engineer,build,555-0101\n'
  + 'Grace Hopper,grace.hopper,BD Director,bd,555-0102\n';

const inp = { background: '#0A0F1C', border: '1px solid #1E2D45', color: '#F1F5F9', padding: '5px 8px', borderRadius: 4, fontSize: 11, boxSizing: 'border-box' };

export default function TeamImport({ onClose, onImported }) {
  const [rows, setRows] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState([]);
  const [fileName, setFileName] = useState('');
  const [domain, setDomain] = useState('@chirprobotics.com');
  const [subsidiaries, setSubsidiaries] = useState([]);
  const [subsidiaryId, setSubsidiaryId] = useState('');
  const [autoInvite, setAutoInvite] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    crmService.getSubsidiaries().then(setSubsidiaries).catch(() => setSubsidiaries([]));
  }, []);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(''); setResult(null);
    try {
      const XLSX = await import('xlsx');
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data, { type: 'array' });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const all = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '' });
      const nonEmpty = all.filter(r => r.some(cell => String(cell).trim() !== ''));
      if (nonEmpty.length < 2) { setError('File needs a header row and at least one member row'); return; }
      setHeaders(nonEmpty[0].map(h => String(h).trim()));
      setMapping(nonEmpty[0].map(autoMap));
      setRows(nonEmpty.slice(1));
      setFileName(file.name);
    } catch (err) { setError(`Could not read file: ${err.message}`); }
    e.target.value = '';
  };

  const mappedMembers = () => rows.map(r => {
    const raw = {};
    mapping.forEach((field, i) => {
      if (field && r[i] !== undefined && String(r[i]).trim() !== '') raw[field] = String(r[i]).trim();
    });
    const name = raw.name || [raw.first_name, raw.last_name].filter(Boolean).join(' ');
    let email = raw.email || '';
    if (email && !email.includes('@') && domain.trim()) {
      email = email + (domain.trim().startsWith('@') ? domain.trim() : '@' + domain.trim());
    }
    return { name, email, position_title: raw.position_title, team_group: raw.team_group, phone: raw.phone };
  }).filter(m => m.name);

  const upload = async () => {
    const members = mappedMembers();
    if (members.length === 0) { setError('No usable rows — map a Name (or First+Last) column'); return; }
    if (members.some(m => !m.email)) { setError('Every member needs an email (map the Email column, or use the default domain for username-only values)'); return; }
    setBusy(true); setError('');
    try {
      const totals = { added: 0, skipped: 0, invites: [], errors: [] };
      for (let i = 0; i < members.length; i += 100) {
        const r = await crmService.bulkCreateTeam({
          members: members.slice(i, i + 100),
          subsidiary_id: subsidiaryId ? parseInt(subsidiaryId) : null,
          auto_invite: autoInvite,
        });
        totals.added += r.added; totals.skipped += r.skipped;
        totals.invites.push(...(r.invites || []));
        totals.errors.push(...(r.errors || []).map(e => ({ ...e, row: e.row + i })));
      }
      setResult(totals);
      onImported?.();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'elephantc2_team_template.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#111827', border: '1px solid #1E2D45', borderRadius: 8, width: 760, maxWidth: '95vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column', color: '#F1F5F9' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #1E2D45', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: 14, color: '#22D3EE' }}>Import Team — bring a company into the family</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', fontSize: 18 }}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px' }}>
          {result ? (
            <div style={{ padding: '6px 0' }}>
              <div style={{ textAlign: 'center', marginBottom: 14 }}>
                <div style={{ fontSize: 30, marginBottom: 6 }}>🐘</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#22D3EE' }}>{result.added} members joined the roster</div>
                <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 4 }}>
                  {result.skipped} skipped (already on the roster){result.invites.length > 0 ? ` · ${result.invites.length} invitations created` : ''}
                </div>
              </div>
              {result.invites.length > 0 && (
                <>
                  <div style={{ fontSize: 10, color: '#c4a563', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                    Invitation links — {result.invites.filter(i => i.email_sent).length} emailed; copy any that need hand delivery
                  </div>
                  <textarea readOnly value={result.invites.map(i => `${i.name} <${i.email}>${i.email_sent ? ' (emailed)' : ''}: ${i.invite_url}`).join('\n')}
                    style={{ ...inp, width: '100%', height: 140, fontSize: 10, fontFamily: 'monospace', resize: 'vertical' }}
                    onFocus={e => e.target.select()} />
                </>
              )}
              {result.errors.length > 0 && (
                <div style={{ marginTop: 10, background: '#0A0F1C', border: '1px solid #EF444433', borderRadius: 6, padding: '8px 10px' }}>
                  {result.errors.slice(0, 10).map((e, i) => (
                    <div key={i} style={{ fontSize: 11, color: '#94A3B8' }}>Row {e.row}: {e.error}</div>
                  ))}
                </div>
              )}
              <div style={{ textAlign: 'center', marginTop: 14 }}>
                <button onClick={onClose} style={{ padding: '8px 24px', background: '#22D3EE', color: '#0A0F1C', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>Done</button>
              </div>
            </div>
          ) : !rows ? (
            <div style={{ textAlign: 'center', padding: '26px 0' }}>
              <label style={{ display: 'inline-block', padding: '12px 28px', background: '#0D1F3C', border: '1px dashed #22D3EE66', color: '#22D3EE', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                Choose a .csv, .xlsx, or .xls file
                <input type="file" accept=".csv,.xlsx,.xls" onChange={handleFile} style={{ display: 'none' }} />
              </label>
              <div style={{ fontSize: 11, color: '#6B7280', marginTop: 14, lineHeight: 1.7 }}>
                Columns: name (or first + last), email, position, team, phone.<br />
                Username-only emails get the default domain appended. Teams map automatically<br />
                (build, growth, bd, operations, headquarters) — or leave blank and assign later.
              </div>
              <button onClick={downloadTemplate} style={{ marginTop: 10, background: 'none', border: '1px solid #1E2D45', color: '#94A3B8', borderRadius: 4, padding: '5px 14px', cursor: 'pointer', fontSize: 11 }}>
                Download template CSV
              </button>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, color: '#94A3B8' }}>{fileName} — {rows.length} rows</span>
                <label style={{ fontSize: 11, color: '#94A3B8', display: 'flex', gap: 6, alignItems: 'center' }}>
                  Default domain
                  <input style={{ ...inp, width: 170 }} value={domain} onChange={e => setDomain(e.target.value)} placeholder="@company.com" />
                </label>
                <select value={subsidiaryId} onChange={e => setSubsidiaryId(e.target.value)} style={{ ...inp, width: 160 }}>
                  <option value="">No subsidiary</option>
                  {subsidiaries.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 11, color: '#94A3B8', cursor: 'pointer' }}>
                  <input type="checkbox" checked={autoInvite} onChange={e => setAutoInvite(e.target.checked)} />
                  Send invitations
                </label>
                <button onClick={() => { setRows(null); setHeaders([]); setMapping([]); }} style={{ marginLeft: 'auto', background: 'none', border: '1px solid #1E2D45', color: '#6B7280', borderRadius: 4, padding: '3px 10px', cursor: 'pointer', fontSize: 10 }}>
                  Choose different file
                </button>
              </div>

              <div style={{ overflowX: 'auto', border: '1px solid #1E2D45', borderRadius: 6 }}>
                <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: '100%' }}>
                  <thead>
                    <tr>
                      {headers.map((h, i) => (
                        <th key={i} style={{ padding: '6px 8px', background: '#0A0F1C', borderBottom: '1px solid #1E2D45', textAlign: 'left', minWidth: 110 }}>
                          <div style={{ color: '#6B7280', fontSize: 9, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h || `(col ${i + 1})`}</div>
                          <select value={mapping[i] || ''} onChange={e => setMapping(m => m.map((v, idx) => idx === i ? e.target.value : v))} style={{ ...inp, width: '100%' }}>
                            <option value="">— skip —</option>
                            {FIELDS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                          </select>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 5).map((r, ri) => (
                      <tr key={ri}>
                        {headers.map((_, ci) => (
                          <td key={ci} style={{ padding: '4px 8px', borderBottom: '1px solid #11182766', color: '#94A3B8', whiteSpace: 'nowrap', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {String(r[ci] ?? '')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {rows.length > 5 && <div style={{ fontSize: 10, color: '#4B5563', marginTop: 4 }}>…and {rows.length - 5} more rows</div>}
              <div style={{ fontSize: 10, color: '#6B7280', marginTop: 8 }}>
                Existing roster emails are skipped. Each new member gets a 14-day invitation covering the EULA, platform tour, and account setup.
              </div>
            </>
          )}
          {error && <div style={{ color: '#EF4444', fontSize: 12, marginTop: 10 }}>{error}</div>}
        </div>

        {rows && !result && (
          <div style={{ padding: '10px 18px', borderTop: '1px solid #1E2D45', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={{ padding: '6px 14px', background: 'none', border: '1px solid #1E2D45', color: '#94A3B8', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>Cancel</button>
            <button onClick={upload} disabled={busy} style={{ padding: '6px 16px', background: '#22D3EE', color: '#0A0F1C', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 'bold', fontSize: 12 }}>
              {busy ? 'Importing…' : `Import ${mappedMembers().length} Members`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
